import React, { useEffect } from 'react';
import {
    Form,
    InputNumber,
    Select,
    Radio,
    Button,
    Card,
    Spin,
    Alert,
    Typography,
    Divider,
    Space,
    message,
    Row,
    Col,
} from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
    getCompanyPatrolSettings,
    updateCompanyPatrolSettings,
} from '../../../config/services/patrolService';
import { parseApiError } from '../../../config/services/errorHandler';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useColors from '../../../hooks/useColors';

const { Text, Title } = Typography;
const { Option } = Select;

/**
 * PatrolSettingsPage — Company patrol settings (admin only).
 * Route: /settings/patrol
 */
const PatrolSettingsPage = ({ insideSettings = false }) => {
    const { colorPrimary } = useColors();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const user = useSelector((state) => state.AppReducer?.user);
    const isRole = user?.userRole?.[0];
    const [form] = Form.useForm();
    const retentionAction = Form.useWatch('passDownRetentionAction', form);

    // Guard — admins only (skip when embedded inside Settings tabs)
    useEffect(() => {
        if (!insideSettings && isRole && isRole !== 'admin') {
            navigate('/');
        }
    }, [isRole, insideSettings]);

    const { data: settings, isLoading } = useQuery(
        ['patrol-settings'],
        getCompanyPatrolSettings,
        {
            onError: (err) => message.error(parseApiError(err).message),
        }
    );

    // Populate form when data loads
    useEffect(() => {
        if (settings) {
            form.setFieldsValue({
                supervisorPatrolAccess: settings.supervisorPatrolAccess,
                passDownVisibilityHours: settings.passDownVisibilityHours,
                passDownRetentionDays: settings.passDownRetentionDays,
                passDownRetentionAction: settings.passDownRetentionAction,
                scanRadiusMeters: settings.scanRadiusMeters,
            });
        }
    }, [settings]);

    const saveMutation = useMutation(
        (body) => updateCompanyPatrolSettings(body),
        {
            onSuccess: () => {
                message.success('Patrol settings saved.');
                queryClient.invalidateQueries(['patrol-settings']);
            },
            onError: (err) => message.error(parseApiError(err).message),
        }
    );

    const handleSave = (values) => {
        // Always submit the full form as per spec
        saveMutation.mutate({
            supervisorPatrolAccess: values.supervisorPatrolAccess,
            passDownVisibilityHours: values.passDownVisibilityHours,
            passDownRetentionDays: values.passDownRetentionDays,
            passDownRetentionAction: values.passDownRetentionAction,
            scanRadiusMeters: values.scanRadiusMeters ?? null,
        });
    };

    if (isRole !== 'admin') return null;

    return (
        <div style={{ maxWidth: '100%' }}>
            <div style={{ marginBottom: 20 }}>
                <Title level={4} style={{ marginBottom: 4 }}>Patrol Settings</Title>
                <Text type="secondary">Company-wide defaults for patrol behaviour. Admin only.</Text>
            </div>

            <Spin spinning={isLoading}>
                {/* component={false}: this page is embedded inside Settings' own <form> (insideSettings).
                    A nested <form> is invalid HTML and swallows the native submit event before
                    onFinish fires, so we render without a <form> tag and submit imperatively instead. */}
                <Form form={form} layout="vertical" onFinish={handleSave} component={false}>
                    {/* Supervisor Access */}
                    <Card
                        title="Supervisor Patrol Access"
                        size="small"
                        style={{ marginBottom: 16, borderRadius: 10, width: '100%' }}
                    >
                        <Form.Item
                            name="supervisorPatrolAccess"
                            rules={[{ required: true, message: 'Required.' }]}
                            style={{ marginBottom: 0 }}
                        >
                            <Radio.Group>
                                <Space direction="vertical">
                                    <Radio value="assigned_only">
                                        <div>
                                            <Text strong>Assigned properties only</Text>
                                            <div><Text type="secondary" style={{ fontSize: 12 }}>Supervisors can patrol only the sites they are explicitly assigned to.</Text></div>
                                        </div>
                                    </Radio>
                                    <Radio value="all_company_properties">
                                        <div>
                                            <Text strong>All company properties</Text>
                                            <div><Text type="secondary" style={{ fontSize: 12 }}>Supervisors may patrol any property in their guard company.</Text></div>
                                        </div>
                                    </Radio>
                                </Space>
                            </Radio.Group>
                        </Form.Item>
                    </Card>

                    {/* Pass-down notes */}
                    <Card
                        title="Pass-down Notes"
                        size="small"
                        style={{ marginBottom: 16, borderRadius: 10 }}
                    >
                        <Row gutter={16}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="Guard Visibility (hours)"
                                    name="passDownVisibilityHours"
                                    extra="How long guards see a pass-down note. 1–8,760 hours."
                                    rules={[
                                        { required: true, message: 'Required.' },
                                        { type: 'integer', min: 1, max: 8760, message: '1–8,760 hours.' },
                                    ]}
                                >
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        min={1}
                                        max={8760}
                                        addonAfter="hours"
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="Retention Period (days)"
                                    name="passDownRetentionDays"
                                    extra="How long the note is kept before the retention action. 1–3,650 days."
                                    rules={[
                                        { required: true, message: 'Required.' },
                                        { type: 'integer', min: 1, max: 3650, message: '1–3,650 days.' },
                                    ]}
                                >
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        min={1}
                                        max={3650}
                                        addonAfter="days"
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={24}>
                                <Form.Item
                                    label="Retention Action"
                                    name="passDownRetentionAction"
                                    rules={[{ required: true, message: 'Required.' }]}
                                >
                                    <Radio.Group>
                                        <Space direction="vertical">
                                            <Radio value="archive">
                                                <Text strong>Archive</Text>
                                                <Text type="secondary" style={{ fontSize: 12, marginLeft: 6 }}>
                                                    Retain note and audit attribution outside active views.
                                                </Text>
                                            </Radio>
                                            {/* <Radio value="anonymize">
                                                <Text strong>Anonymize</Text>
                                                <Text type="secondary" style={{ fontSize: 12, marginLeft: 6 }}>
                                                    Retain content but remove author attribution during enforcement.
                                                </Text>
                                            </Radio> */}
                                            <Radio value="delete">
                                                <Text strong style={{ color: '#ef4444' }}>Delete</Text>
                                                <Text type="secondary" style={{ fontSize: 12, marginLeft: 6 }}>
                                                    Permanently remove after retention period.
                                                </Text>
                                            </Radio>
                                        </Space>
                                    </Radio.Group>
                                </Form.Item>
                                {retentionAction === 'delete' && (
                                    <Alert
                                        type="error"
                                        showIcon
                                        icon={<WarningOutlined />}
                                        message="Destructive: Notes will be permanently deleted after the retention period. This cannot be undone."
                                        style={{ marginBottom: 12 }}
                                    />
                                )}
                            </Col>
                        </Row>
                    </Card>

                    {/* Default scan radius */}
                    <Card
                        title="Default Scan Radius"
                        size="small"
                        style={{ marginBottom: 16, borderRadius: 10 }}
                    >
                        <Form.Item
                            label="Scan Radius (metres)"
                            name="scanRadiusMeters"
                            extra="Company default. Can be overridden per property or per checkpoint. Checkpoint-level is most specific."
                            rules={[
                                { type: 'integer', min: 10, max: 5000, message: '10–5,000 metres.' },
                            ]}
                        >
                            <InputNumber
                                style={{ width: 200 }}
                                min={10}
                                max={5000}
                                addonAfter="m"
                            />
                        </Form.Item>
                    </Card>

                    {/* Property overrides note */}
                    <Alert
                        type="info"
                        showIcon
                        message="Property-level overrides"
                        description="Property-specific patrol settings can be updated via PATCH /api/properties/{id}/patrol-settings. Note: there is currently no GET endpoint for property settings, so values cannot be pre-filled after reload. Only company-level settings above are fully readable."
                        style={{ marginBottom: 20 }}
                    />

                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            type="primary"
                            onClick={() => form.submit()}
                            loading={saveMutation.isLoading}
                            style={{
                                backgroundColor: colorPrimary,
                                border: 'none',
                                color: '#fff',
                                height: '40px',
                                borderRadius: '6px',
                                fontWeight: 600,
                                padding: '0 24px',
                                boxShadow: 'none',
                            }}
                        >
                            Save Settings
                        </Button>
                    </div>
                </Form>
            </Spin>
        </div>
    );
};

export default PatrolSettingsPage;
